<template>
  <div class="address-manager">
    <!-- Header with close button -->
    <div class="flex justify-between items-center my-4">
      <h3 class="text-base font-medium text-gray-900 dark:text-gray-100">
        Gestionar Direcciones
      </h3>
      <button
        class="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex items-center justify-center"
        title="Cerrar"
        @click="$emit('close')"
      >
        <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
    
    <div class="space-y-4">
      <!-- Address List -->
      <div
        v-if="addresses.length > 0"
        class="space-y-3"
      >
        <AddressCard
          v-for="address in addresses"
          :key="address.id"
          :address="address"
          :is-selected="selectedAddressId === address.id"
          :total-addresses="addresses.length"
          @set-default="handleSetDefault"
          @edit="handleEdit"
          @delete="handleDelete"
          @select="handleSelect"
        />
      </div>

      <!-- No Addresses -->
      <AddressEmptyState v-else />

      <!-- Add New Address Button -->
      <button
        class="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors flex items-center justify-center"
        @click="startAdding"
      >
        <svg
          class="w-5 h-5 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
        Agregar Nueva Dirección
      </button>
    </div>

    <!-- Add/Edit Address Form -->
    <AddressForm
      v-if="showAddForm"
      :is-editing="!!editingAddress"
      :is-submitting="isSubmitting"
      :form-data="addressForm"
      @submit="handleSave"
      @cancel="cancelForm"
      @update:address="addressForm.address = $event"
      @update:apartment="addressForm.apartment = $event"
      @update:neighborhood="addressForm.neighborhood = $event"
      @update:city="addressForm.city = $event"
      @update:postal_code="addressForm.postal_code = $event"
      @update:reference_point="addressForm.reference_point = $event"
      @update:is_default="addressForm.is_default = $event"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useAddressService } from '@/composables/useAddressService';
import { useAddressForm } from '@/composables/useAddressForm';
import AddressCard from '@/components/orders/AddressCard.vue';
import AddressForm from '@/components/orders/AddressForm.vue';
import AddressEmptyState from '@/components/orders/AddressEmptyState.vue';
import type { CustomerAddress } from '@/types/customer';

interface Props {
  customerId: number;
  selectedAddressId?: number;
}

interface Emits {
  (e: 'address-selected', address: CustomerAddress): void;
  (e: 'address-updated'): void;
  (e: 'close'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Use composables
const { addresses, loadAddresses, createAddress, updateAddress, deleteAddress, setDefaultAddress } = useAddressService();
const { showAddForm, editingAddress, isSubmitting, addressForm, startEditing, cancelForm, startAdding, getFormData, validateForm } = useAddressForm();

// Event handlers
const handleSetDefault = async (addressId: number) => {
  try {
    await setDefaultAddress(addressId, props.customerId);
    emit('address-updated');
  } catch (error) {
    console.error('Error setting default address:', error);
  }
};

const handleEdit = (address: CustomerAddress) => {
  startEditing(address);
};

const handleDelete = async (addressId: number) => {
  if (!confirm('¿Estás seguro de que quieres eliminar esta dirección?')) {
    return;
  }
  
  try {
    await deleteAddress(addressId, props.customerId);
    emit('address-updated');
  } catch (error) {
    console.error('Error deleting address:', error);
  }
};

const handleSelect = (address: CustomerAddress) => {
  emit('address-selected', address);
};

const handleSave = async () => {
  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;
  try {
    const formData = getFormData();
    
    if (editingAddress.value) {
      await updateAddress(editingAddress.value.id, props.customerId, formData);
    } else {
      await createAddress(props.customerId, formData);
    }
    
    cancelForm();
    emit('address-updated');
  } catch (error) {
    console.error('Error saving address:', error);
  } finally {
    isSubmitting.value = false;
  }
};

// Initialize
onMounted(() => {
  loadAddresses(props.customerId);
});
</script>

<style scoped>
.address-manager {
  max-width: 600px;
}
</style>
